// Formatting helpers shared by views.
import type { Actor, Item, Priority, StatusCategory } from "../api/types";
import { resolveDate } from "../editor/tasktokens";
import { addDaysYmd, diffDaysYmd, getDisplayTimeZone, todayYmd, zonedHm, zonedToDate, zonedYmd } from "./tz";

/**
 * ASSUMPTION (dates): a date-only due/start (`due_has_time=false`) is stored as midnight UTC of
 * that calendar date (`YYYY-MM-DDT00:00:00Z`) and read back via its UTC date part. Dates with a
 * time are real instants, shown/entered in the user's display time zone (profile setting,
 * fallback browser zone; see ./tz).
 */
export function partsToIso(date: string, time: string | null): string {
  if (time) return zonedToDate(date, time).toISOString();
  return `${date}T00:00:00Z`;
}

export function isoToParts(iso: string, hasTime: boolean): { date: string; time: string | null } {
  if (!hasTime) return { date: iso.slice(0, 10), time: null };
  const d = new Date(iso);
  return { date: zonedYmd(d), time: zonedHm(d) };
}

/** Convert a task-token date value (due:fri) to ISO + has_time for the API. */
export function tokenDateToIso(value: string): { iso: string; hasTime: boolean } | null {
  const r = resolveDate(value, todayAsLocalDate());
  if (!r) return null;
  return { iso: partsToIso(r.date, r.time), hasTime: r.time !== null };
}

/** A local Date whose calendar date equals "today" in the display zone (for resolveDate). */
export function todayAsLocalDate(now = new Date()): Date {
  const [y, m, d] = todayYmd(now).split("-").map(Number);
  return new Date(y, m - 1, d, 12);
}

/** Instant marking the end of the due moment (end of day in the display zone when no time). */
export function dueMoment(item: Pick<Item, "due_at" | "due_has_time">): Date | null {
  if (!item.due_at) return null;
  if (item.due_has_time) return new Date(item.due_at);
  return new Date(zonedToDate(item.due_at.slice(0, 10), "23:59").getTime() + 59999);
}

/** Calendar date (YYYY-MM-DD) an item date falls on for the user. */
export function itemYmd(iso: string, hasTime: boolean): string {
  return hasTime ? zonedYmd(new Date(iso)) : iso.slice(0, 10);
}

/** Start dates carry no has_time flag: a non-midnight-UTC value is treated as having a time. */
export function startHasTime(iso: string | null): boolean {
  return !!iso && !iso.endsWith("T00:00:00Z") && !iso.endsWith("T00:00:00.000Z");
}

/** Move an item date by whole calendar days, keeping the time of day (in the display zone). */
export function shiftIsoDays(iso: string, hasTime: boolean, days: number): string {
  if (!hasTime) return `${addDaysYmd(iso.slice(0, 10), days)}T00:00:00Z`;
  const p = isoToParts(iso, true);
  return partsToIso(addDaysYmd(p.date, days), p.time);
}

/** Put an item date on another calendar day, keeping the time of day. */
export function moveIsoToDay(iso: string | null, hasTime: boolean, ymd: string): string {
  if (!iso || !hasTime) return `${ymd}T00:00:00Z`;
  return partsToIso(ymd, isoToParts(iso, true).time);
}

export function isClosed(cat: StatusCategory): boolean {
  return cat === "done" || cat === "cancelled";
}

export function isItemOverdue(item: Item, now = new Date()): boolean {
  if (isClosed(item.status_category)) return false;
  const m = dueMoment(item);
  return !!m && m.getTime() < now.getTime();
}

type FmtKind = "day" | "dayYear" | "time" | "weekday";
const fmtCache = new Map<string, Intl.DateTimeFormat>();
/** Formatter for calendar dates (UTC-noon based, zone independent) or times (display zone). */
function fmt(kind: FmtKind): Intl.DateTimeFormat {
  const tz = kind === "time" ? getDisplayTimeZone() : "UTC";
  const key = `${kind}|${tz}`;
  let f = fmtCache.get(key);
  if (!f) {
    const opts: Intl.DateTimeFormatOptions =
      kind === "day"
        ? { month: "short", day: "numeric" }
        : kind === "dayYear"
          ? { month: "short", day: "numeric", year: "numeric" }
          : kind === "weekday"
            ? { weekday: "short" }
            : { hour: "2-digit", minute: "2-digit" };
    f = new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz });
    fmtCache.set(key, f);
  }
  return f;
}

function utcNoon(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** Format a calendar date with Intl options (zone independent). */
export function formatYmdWith(ymd: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: "UTC" }).format(utcNoon(ymd));
}

/** Human short label for a calendar date: Today / Tomorrow / Mon / Oct 31 / Oct 31, 2027. */
export function humanDate(ymd: string, now = new Date()): string {
  const today = todayYmd(now);
  const diff = diffDaysYmd(today, ymd);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff < 7) return fmt("weekday").format(utcNoon(ymd));
  if (ymd.slice(0, 4) === today.slice(0, 4)) return fmt("day").format(utcNoon(ymd));
  return fmt("dayYear").format(utcNoon(ymd));
}

export function formatItemDate(iso: string | null, hasTime: boolean): string {
  if (!iso) return "";
  const p = isoToParts(iso, hasTime);
  const day = humanDate(p.date);
  if (!p.time) return day;
  return `${day} ${fmt("time").format(new Date(iso))}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${humanDate(zonedYmd(d))} ${fmt("time").format(d)}`;
}

export function formatTime(iso: string | null): string {
  return iso ? fmt("time").format(new Date(iso)) : "";
}

export function initials(name: string): string {
  const parts = name.trim().split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Handle used for @mentions: local part of the email, else display name without spaces. */
export function actorHandle(actor: Actor): string {
  if (actor.primary_email) return actor.primary_email.split("@")[0].toLowerCase();
  return actor.display_name.toLowerCase().replace(/\s+/g, ".");
}

export function matchActorByHandle(actors: Actor[], handle: string): Actor | undefined {
  const h = handle.toLowerCase();
  return (
    actors.find((a) => actorHandle(a) === h) ??
    actors.find((a) => a.primary_email?.toLowerCase() === h) ??
    actors.find((a) => a.display_name.toLowerCase() === h) ??
    actors.find((a) => a.display_name.toLowerCase().split(/\s+/)[0] === h)
  );
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  0: "None",
  1: "Low",
  2: "Normal",
  3: "High",
  4: "Urgent",
};

export const CATEGORY_LABELS: Record<StatusCategory, string> = {
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

const CONNECTOR_LABELS: Record<string, string> = {
  "nextcloud-deck": "Deck",
  "msgraph-todo": "To Do",
  trello: "Trello",
  jira: "Jira",
};

export function connectorLabel(type: string): string {
  return CONNECTOR_LABELS[type] ?? type;
}

/** Fractional key strictly between a and b (lexicographic), used for optimistic positions only. */
export function keyBetween(a: string | null, b: string | null): string {
  const lo = a ?? "";
  if (b === null || b === "") return lo + "V";
  if (lo >= b) return lo + "V"; // inconsistent input: anything after a
  let prefix = "";
  for (let i = 0; ; i++) {
    const ca = i < lo.length ? lo.charCodeAt(i) : 32;
    const cb = i < b.length ? b.charCodeAt(i) : 127;
    if (ca === cb) {
      prefix += String.fromCharCode(ca);
      continue;
    }
    if (cb - ca > 1) return prefix + String.fromCharCode(Math.floor((ca + cb) / 2));
    // Adjacent characters: keep a's char and append something greater than a's remainder.
    return prefix + String.fromCharCode(ca) + (i + 1 < lo.length ? lo.slice(i + 1) : "") + "V";
  }
}
